import { useEffect, useState } from "react";
import { Bell, Check, Trash2 } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { GlassCard, PageHeader, EmptyState } from "../components/shared";
import { toast } from "sonner";

export default function Notifications() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get("/notifications");
            setItems(data.items || []);
        } catch {} finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const readAll = async () => {
        await api.post("/notifications/read-all");
        load();
    };

    const markRead = async (id) => {
        await api.post(`/notifications/${id}/read`);
        load();
    };

    const deleteNotif = async (id) => {
        try {
            await api.delete(`/notifications/${id}`);
            toast.success("Notification deleted");
            load();
        } catch (err) {
            toast.error("Failed to delete notification");
        }
    };

    const deleteAll = async () => {
        if (!window.confirm("Are you sure you want to delete all notifications?")) return;
        try {
            await api.post("/notifications/delete-all");
            toast.success("All notifications deleted");
            load();
        } catch {
            toast.error("Failed to delete notifications");
        }
    };

    return (
        <div>
            <PageHeader
                title="Notifications"
                subtitle="Stay up to date on team activity."
                testId="notifications-header"
                actions={
                    <div className="flex items-center gap-2">
                        <Button variant="outline" onClick={readAll} className="rounded-full" data-testid="read-all-btn">
                            <Check className="w-4 h-4 mr-1" /> Read all
                        </Button>
                        <Button 
                            variant="outline" 
                            onClick={deleteAll} 
                            className="rounded-full text-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/20" 
                            data-testid="delete-all-btn"
                        >
                            <Trash2 className="w-4 h-4 mr-1" /> Delete all
                        </Button>
                    </div>
                }
            />

            {loading ? <div className="text-center py-8 text-sm text-muted-foreground">Loading…</div> :
            items.length === 0 ? (
                <GlassCard><EmptyState icon={Bell} title="No notifications" description="You're all caught up." /></GlassCard>
            ) : (
                <div className="space-y-2">
                    {items.map((n) => (
                        <GlassCard key={n.id} className={`p-5 flex items-start gap-3 ${!n.read ? "border-l-4 border-l-primary" : ""}`} data-testid={`notif-${n.id}`}>
                            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary grid place-items-center"><Bell className="w-4 h-4" /></div>
                            <div className="flex-1 min-w-0">
                                <div className="font-medium">{n.title}</div>
                                <div className="text-sm text-muted-foreground">{n.body || n.message}</div>
                                <div className="text-[11px] text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString()}</div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0 self-center">
                                {!n.read && (
                                    <button onClick={() => markRead(n.id)} className="text-xs text-primary hover:underline whitespace-nowrap" data-testid={`mark-read-${n.id}`}>Mark read</button>
                                )}
                                <button onClick={() => deleteNotif(n.id)} className="text-xs text-rose-500 hover:text-rose-700 whitespace-nowrap flex items-center gap-1" data-testid={`delete-notif-${n.id}`}>
                                    <Trash2 className="w-3.5 h-3.5" /> Delete
                                </button>
                            </div>
                        </GlassCard>
                    ))}
                </div>
            )}
        </div>
    );
}
